import { uploadMedia } from "@/lib/api/media.functions";
import { arrayBufferToBase64 } from "@/lib/media/encode";

export const MEDIA_UPLOAD_ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
] as const;

export const MEDIA_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

export function validateMediaUploadFile(file: File): string | null {
  if (!MEDIA_UPLOAD_ALLOWED_TYPES.includes(file.type as (typeof MEDIA_UPLOAD_ALLOWED_TYPES)[number])) {
    return "Only PNG, JPG, WebP, SVG allowed";
  }
  if (file.size > MEDIA_UPLOAD_MAX_BYTES) {
    return "Max 5MB";
  }
  return null;
}

export async function uploadMediaFile(file: File) {
  const error = validateMediaUploadFile(file);
  if (error) throw new Error(error);
  const buffer = await file.arrayBuffer();
  const dataBase64 = arrayBufferToBase64(buffer);
  return uploadMedia({
    data: { filename: file.name, mimeType: file.type, dataBase64 },
  });
}

export type MediaUploadBatchResult = {
  uploaded: Awaited<ReturnType<typeof uploadMediaFile>>[];
  failed: { filename: string; error: string }[];
};

export async function uploadMediaFiles(files: File[]): Promise<MediaUploadBatchResult> {
  const uploaded: MediaUploadBatchResult["uploaded"] = [];
  const failed: MediaUploadBatchResult["failed"] = [];

  for (const file of files) {
    const validationError = validateMediaUploadFile(file);
    if (validationError) {
      failed.push({ filename: file.name, error: validationError });
      continue;
    }
    try {
      uploaded.push(await uploadMediaFile(file));
    } catch (e) {
      failed.push({
        filename: file.name,
        error: e instanceof Error ? e.message : "Upload failed",
      });
    }
  }

  return { uploaded, failed };
}

export function mediaUploadResultMessage(result: MediaUploadBatchResult): string {
  const { uploaded, failed } = result;
  if (uploaded.length === 0 && failed.length === 1) {
    return failed[0].error;
  }
  if (uploaded.length === 0) {
    return `No files uploaded${failed.length ? ` (${failed.length} failed)` : ""}`;
  }
  if (failed.length === 0) {
    return uploaded.length === 1 ? "Uploaded" : `Uploaded ${uploaded.length} images`;
  }
  return `Uploaded ${uploaded.length}, ${failed.length} failed`;
}
